import type { App, BuildStatus, Environment, Platform } from '@hangar/shared';
import type { BuildsSearch } from '@/router';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OptionSelect } from '@/components/OptionSelect';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ALL_STATUSES, STATUS_LABEL } from './buildHelpers';
import { statusSummary, statusesOf, toggleStatus } from './buildsFiltersModel';

interface ControlProps {
  search: BuildsSearch;
  onChange: (next: BuildsSearch) => void;
  /** Replaces the fixed bar width (the phone sheet stretches each control to the full width). */
  className?: string;
  /** `sm` (32 px) in the desktop row; `default` in the phone sheet; every trigger is 44 px on phones. */
  size?: 'sm' | 'default';
}

export function AppSelect({ size = 'sm', search, onChange, apps, className }: ControlProps & { apps: App[] | undefined }) {
  return (
    <div className={cn('w-44 shrink-0', className)}>
      <OptionSelect
        data-testid="builds-filter-app"
        size={size}
        aria-label="App"
        value={search.app ?? ''}
        onValueChange={(app) => onChange({ ...search, app: app || undefined })}
        options={[{ value: '', label: 'App: all' }, ...(apps ?? []).map((a) => ({ value: a.slug, label: a.slug }))]}
        className="font-mono text-[13px]"
        contentClassName="font-mono"
      />
    </div>
  );
}

export function EnvironmentSelect({ size = 'sm', search, onChange, environments, className }: ControlProps & { environments: Environment[] }) {
  return (
    <div className={cn('w-44 shrink-0', className)}>
      <OptionSelect
        data-testid="builds-filter-environment"
        size={size}
        aria-label="Environment"
        value={search.environment ?? ''}
        onValueChange={(environment) => onChange({ ...search, environment: environment || undefined })}
        options={[{ value: '', label: 'Environment: all' }, ...environments.map((env) => ({ value: env.slug, label: env.name }))]}
        className="text-[13px]"
      />
    </div>
  );
}

const PLATFORM_FILTER_OPTIONS = [
  { value: '', label: 'Platform: all' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];

export function PlatformSelect({ size = 'sm', search, onChange, className }: ControlProps) {
  return (
    <div className={cn('w-36 shrink-0', className)}>
      <OptionSelect
        data-testid="builds-filter-platform"
        size={size}
        aria-label="Platform"
        value={search.platform ?? ''}
        onValueChange={(platform) => onChange({ ...search, platform: (platform || undefined) as Platform | undefined })}
        options={PLATFORM_FILTER_OPTIONS}
        className="text-[13px]"
      />
    </div>
  );
}

export function StatusFilter({ search, onChange, className }: ControlProps) {
  const statuses = statusesOf(search.status);
  const setStatus = (s: BuildStatus, on: boolean) => onChange({ ...search, status: toggleStatus(statuses, s, on) });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid="builds-filter-status" className={cn('shrink-0 font-normal', className)}>
          Status: {statusSummary(statuses)}
          <Icon name="expand_more" size={16} className="ml-auto text-subtle" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Status</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ALL_STATUSES.map((s) => (
          <DropdownMenuCheckboxItem
            key={s}
            data-testid={`builds-status-${s}`}
            checked={statuses.includes(s)}
            onCheckedChange={(v) => setStatus(s, v === true)}
            onSelect={(e) => e.preventDefault()}
          >
            {STATUS_LABEL[s]}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function BranchInput({ value, onChange, className }: { value: string; onChange: (v: string) => void; className?: string }) {
  return (
    <Input
      data-testid="builds-filter-branch"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Branch"
      aria-label="Branch"
      className={cn('h-8 w-36 shrink-0 font-mono text-[13px] max-sm:h-11 sm:text-[13px]', className)}
      spellCheck={false}
    />
  );
}
